/**
 * Component Registry for Haven Realty Capital Theme
 * 
 * Extends the base blue-haven theme registry with Haven Realty Capital specific overrides.
 */

import type { ComponentRegistry } from '@vibesites/runtime';
import { registry as baseRegistry } from '@vibesites/theme-blue-haven/registry';
import { Footer } from './components/Footer';
import { Hero } from './components/Hero';
import { HeroImageBG } from './components/HeroImageBG';
import { ScheduleTourIntro } from './components/ScheduleTourIntro';
import { ScheduleTourForm } from './components/ScheduleTourForm';
import { GuidedTourCTA } from './components/GuidedTourCTA';
import { ScheduleTourInfo } from './components/ScheduleTourInfo';
import { Header } from './components/Header';
import { SitemapContent } from './components/SitemapContent';
import { GalleryIntro } from './components/GalleryIntro';
import { GalleryPhotos } from './components/GalleryPhotos';
import { PropertyOverview } from './components/PropertyOverview';
import { DiscoverAmenities } from './components/DiscoverAmenities';
import { PinnedCallouts } from './components/PinnedCallouts';
import { TabbedCallouts } from './components/TabbedCallouts';
import { FloorPlansIntro } from './components/FloorPlansIntro';
import { VirtualTourIntro } from './components/VirtualTourIntro';
import { AmenitiesIntro } from './components/AmenitiesIntro';
import { AmenitiesFeatures } from './components/AmenitiesFeatures';
import { NeighborhoodIntro } from './components/NeighborhoodIntro';
import { InteractiveMapIntro } from './components/InteractiveMapIntro';
import { SiteMapIntro } from './components/SiteMapIntro';
import { SiteMapImage } from './components/SiteMapImage';
import { PetPolicyIntro } from './components/PetPolicyIntro';
import { PetPolicy } from './components/PetPolicy';
import { ContactForm } from './components/ContactForm';
import { ApplyNowOptions } from './components/ApplyNowOptions';
import { ResidentAccess } from './components/ResidentAccess';
import { FaqIntro } from './components/FaqIntro';
import { FaqList } from './components/FaqList';
import { PreferredEmployerIntro } from './components/PreferredEmployerIntro';
import { PreferredEmployerBenefits } from './components/PreferredEmployerBenefits';
import { PreferredEmployerList } from './components/PreferredEmployerList';
import { PreferredEmployerCTA } from './components/PreferredEmployerCTA';
import { ThankYouContent } from './components/ThankYouContent';
import { HyperlocalHero } from './components/HyperlocalHero';
import { HyperlocalPageCopy } from './components/HyperlocalPageCopy';
import { PrivacyPolicyContent } from './components/PrivacyPolicyContent';

/**
 * Haven Realty Capital specific component overrides.
 * These components have been customized for the Haven Realty Capital brand.
 */
const havenrealtycapitalOverrides: Partial<ComponentRegistry> = {
  Footer,
  Hero,
  HeroImageBG,
  ScheduleTourIntro,
  ScheduleTourForm,
  GuidedTourCTA,
  ScheduleTourInfo,
  Header,
  SitemapContent,
  GalleryIntro,
  GalleryPhotos,
  PropertyOverview,
  DiscoverAmenities,
  PinnedCallouts,
  TabbedCallouts,
  FloorPlansIntro,
  VirtualTourIntro,
  AmenitiesIntro,
  AmenitiesFeatures,
  NeighborhoodIntro,
  InteractiveMapIntro,
  SiteMapIntro,
  SiteMapImage,
  PetPolicyIntro,
  PetPolicy,
  ContactForm,
  ApplyNowOptions,
  ResidentAccess,
  FaqIntro,
  FaqList,
  PreferredEmployerIntro,
  PreferredEmployerBenefits,
  PreferredEmployerList,
  PreferredEmployerCTA,
  ThankYouContent,
  HyperlocalHero,
  HyperlocalPageCopy,
  PrivacyPolicyContent,
};

/**
 * Final merged registry for Haven Realty Capital properties.
 * Inherits all components from the blue-haven theme, with Haven Realty Capital overrides.
 */
export const registry: ComponentRegistry = {
  ...baseRegistry,
  ...havenrealtycapitalOverrides,
};

export default registry;
